import type { Channel, ChannelType, Preferences } from "sdk";
import type { SetStoreFunction } from "solid-js/store";
import type { ChannelState } from "@/contexts/channel";

/**
 * Get the channel type category for thread sidebar preferences
 */
export function getChannelTypeCategory(
	channelType: ChannelType,
): "text" | "document" | "forum" | null {
	if (
		channelType === "Text" ||
		channelType === "Announcement" ||
		channelType === "Dm" ||
		channelType === "Gdm"
	) {
		return "text";
	}
	if (channelType === "Document" || channelType === "Wiki") {
		return "document";
	}
	if (channelType === "Forum" || channelType === "Forum2") {
		return "forum";
	}
	return null;
}

/**
 * Map a channel type to the corresponding thread sidebar preference
 */
const THREAD_SIDEBAR_PREFS = {
	text: "threads_sidebar_text",
	document: "threads_sidebar_document",
	forum: "threads_sidebar_forum",
} as const;

/**
 * Check if threads should open in sidebar for a given channel based on user preferences
 */
export function shouldUseThreadSidebar(
	channel: Channel,
	preferences: Preferences,
): boolean {
	const category = getChannelTypeCategory(channel.type);
	if (!category) return false;

	return preferences.frontend[THREAD_SIDEBAR_PREFS[category]];
}

/**
 * Open a thread in the sidebar if the preference is enabled, otherwise navigate to the thread page.
 * If the thread is already open in the sidebar, close it.
 */
export function openThread(
	thread: Channel,
	parentChannel: Channel,
	preferences: Preferences,
	setChannelState: SetStoreFunction<ChannelState> | null,
	navigate: (path: string) => void,
) {
	const shouldUseSidebar = shouldUseThreadSidebar(parentChannel, preferences);
	if (shouldUseSidebar && setChannelState) {
		setChannelState((prev) => ({
			thread_chat_sidebar_thread_id:
				prev.thread_chat_sidebar_thread_id === thread.id
					? undefined
					: thread.id,
		}));
	} else {
		navigate(`/thread/${thread.id}`);
	}
}
